import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';

import PropTypes, { Validator } from 'prop-types';

import { getCurrentToken, getAccHostname } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import Illustration from '@adsk/alloy-react-illustration';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';
import { useSideNavigation } from '@adsk/alloy-side-navigation';
import TrialBanner from '@adsk/alloy-trial-banner';

import { ANALYTICS_TYPES } from './helpers/analytics';
import {
  accountsListShape,
  projectsListShape,
  productsListShape,
  projectShape,
  userShape,
} from './helpers/propTypes';
import { Account, Product, Project, User } from './helpers/types';
import EnvLabel from './layout/EnvLabel';
import Header from './layout/Header';
import ProductButton from './layout/ProductButton';
import ProfileButton from './layout/ProfileButton';
import ProjectButton from './layout/ProjectButton';
import QuestionMarkMenu from './layout/QuestionMarkMenu';
import TopBar from './layout/TopBar';

type HelpLinksType = ComponentProps<typeof QuestionMarkMenu>['helpLinks'];

type TopNavigationProps = StylableComponent<HTMLDivElement> & {
  logo?: boolean;
  login?: boolean;
  admin?: boolean;
  showHelpMenu?: boolean;
  projectId?: string;
  accountId?: string;
  currentProductId?: string;
  currentProject?: Project;
  accounts?: Account[];
  products?: Product[];
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projects?: Project[];
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_templates?: Project[];
  user?: User | null;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsLoading?: boolean;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsError?: boolean;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsRetry?: () => void;
  onLogout?: () => void;
  helpLinks?: HelpLinksType;
  showTrialBanner?: boolean;
  showSubscriptionsModal?: boolean;
  isProductRequired?: boolean;
  renderCustomProductHeader?: () => ReactNode;
  onProductClick?: ({ id, url }: { [key: string]: string }) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  onAccountClick?: (account?: object, url?: string) => void;
};

/**
 * Acs Unified TopNavigation
 */
const TopNavigation: FC<TopNavigationProps> = (props) => {
  const {
    style,
    className,
    logo,
    login,
    admin,
    showHelpMenu,
    projectId,
    accountId,
    currentProductId,
    currentProject,
    accounts,
    products,
    UNSAFE_projects: projects,
    UNSAFE_templates: templates,
    user,
    UNSAFE_projectsLoading: projectsLoading,
    UNSAFE_projectsError: projectsError,
    UNSAFE_projectsRetry: projectsRetry,
    onLogout,
    helpLinks,
    showTrialBanner,
    showSubscriptionsModal,
    isProductRequired,
    renderCustomProductHeader,
    onProductClick,
    onProjectClick,
    onAccountClick,
  } = props;

  useEffect(
    () =>
      analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_VIEW, {
        package_name: 'TopNavigation',
      }),
    [],
  );

  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { collapsed } = useSideNavigation();
  const onLogoutWithSolvvy = useCallback(() => {
    const { solvvyApi } = window;
    solvvyApi?.clearSolvvyState && solvvyApi.clearSolvvyState();
    onLogout && onLogout();
  }, [onLogout]);

  const { value: subscriptionsModalFF } = useAlloyFeatureFlag(
    'subscriptions-modal',
  );

  const userHomeUrl = getAccHostname();

  return (
    <div
      className={className}
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        },
        style,
      ]}
    >
      <TopBar hideLogo={logo} admin={admin} />
      <EnvLabel />
      <Header
        left={
          <>
            {logo && (
              <a
                data-testid="TopNavigation__Header__Logo__a"
                href={userHomeUrl}
              >
                <Illustration
                  type={Illustration.TYPES.ACC_TM_FULL_SINGLE_NEW_2022_BLACK}
                  style={{
                    verticalAlign: 'middle',
                    margin: '0 16px',
                  }}
                />
              </a>
            )}
            {'products' in props ? (
              <ProductButton
                productId={currentProductId}
                products={products}
                required={isProductRequired}
                renderCustomHeader={renderCustomProductHeader}
                onProductClick={onProductClick}
              />
            ) : null}

            {!collapsed && 'products' in props && (
              <Divider
                color={collapsed ? theme.colors.white : undefined}
                variant={Divider.VARIANTS.VERTICAL}
                style={{
                  display: 'inline-block',
                  height: 32,
                  margin: '0 16px',
                  [theme.mediaQuery.small]: {
                    display: 'none',
                  },
                }}
              />
            )}

            {'accounts' in props ||
            'UNSAFE_projects' in props ||
            'UNSAFE_templates' in props ? (
              <ProjectButton
                accountId={accountId}
                projectId={projectId}
                productId={currentProductId}
                currentProject={currentProject}
                accounts={accounts}
                UNSAFE_projects={projects}
                UNSAFE_templates={templates}
                UNSAFE_projectsLoading={projectsLoading}
                UNSAFE_projectsError={projectsError}
                UNSAFE_projectsRetry={projectsRetry}
                onProjectClick={onProjectClick}
                onAccountClick={onAccountClick}
              />
            ) : null}
          </>
        }
        right={
          <>
            {showTrialBanner && (
              <TrialBanner user={user} currentProductId={currentProductId} />
            )}

            {showHelpMenu && (
              <QuestionMarkMenu
                helpLinks={helpLinks}
                currentProductId={currentProductId}
                isProductRequired={isProductRequired}
              />
            )}

            {(showHelpMenu || showTrialBanner) && (
              <Divider
                variant={Divider.VARIANTS.VERTICAL}
                style={{
                  height: 32,
                  margin: '0 16px',
                }}
              />
            )}

            {'user' in props && (
              <ProfileButton
                user={user}
                projectId={projectId}
                onLogout={onLogoutWithSolvvy}
                showSubscriptionsModal={
                  showSubscriptionsModal || subscriptionsModalFF
                }
              />
            )}

            {login && !('user' in props) && (
              <BasicButton
                style={theme.typography.heading4}
                onClick={() => getCurrentToken()}
              >
                {T.translate('SIGN_IN')}
              </BasicButton>
            )}
          </>
        }
      />
    </div>
  );
};

TopNavigation.displayName = 'TopNavigation';

TopNavigation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** list of current user all projects */
  accounts: accountsListShape,
  /** list of current user all projects */
  UNSAFE_projects: projectsListShape,
  /** list of products a user could move across */
  products: productsListShape,
  /** list of templates */
  UNSAFE_templates: projectsListShape,
  /** The current account ID, to mark as selected in the ProjectPicker */
  accountId: PropTypes.string,
  /** The current project, to mark as selected in the ProjectPicker */
  currentProject: projectShape,
  /** The current product ID, to mark as selected in the ProductPicker */
  currentProductId: PropTypes.string,
  /** current projectId of project or template */
  projectId: PropTypes.string,
  /** current user account details */
  user: userShape,
  /** is projects Entitlements loading */
  UNSAFE_projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  UNSAFE_projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  UNSAFE_projectsRetry: PropTypes.func,
  /** display help menu */
  showHelpMenu: PropTypes.bool,
  /** show subscriptions modal (page level) */
  showSubscriptionsModal: PropTypes.bool,
  /** mode for admin pages */
  admin: PropTypes.bool,
  /** display logo */
  logo: PropTypes.bool,
  /** display login button */
  login: PropTypes.bool,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** Override default help links */
  helpLinks: PropTypes.shape({
    help: PropTypes.string,
    about: PropTypes.string,
  }) as Validator<HelpLinksType>,
  /** display trial banner */
  showTrialBanner: PropTypes.bool,
  /** is product required */
  isProductRequired: PropTypes.bool,
  /** Render custom product picker header */
  renderCustomProductHeader: PropTypes.func,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
};

export default TopNavigation;
